import { prisma } from "client";
import {
  resolveLoggedInUserTypeDecision,
  type LoggedInUserTypeResult,
} from "./loggedInUserType.pure";

export type { LoggedInUserTypeResult, LoggedInUserTypeSuccess } from "./loggedInUserType.pure";
export {
  resolveLoggedInUserTypeDecision,
  typeFromGroups,
} from "./loggedInUserType.pure";

export type GetLoggedInUserTypeParams = {
  email?: string | null;
  eventId?: string;
  eventSlug?: string;
  userId?: string;
};

/**
 * Event-scoped participant type used by web login (`getLoggedInUserType`)
 * and `GET /api/event/[slug]/me`.
 *
 * Queries (all indexed):
 * - Event by slug (@unique) or id (_id)
 * - User by id (_id) or email (prefix of @@unique [email, accessibleWhiteLabelId])
 * - Profile by [eventId, userId]
 * - Exhibitor/Sponsor by [eventId, email] then [eventId, teamIds]
 */
export async function resolveLoggedInUserType(
  params: GetLoggedInUserTypeParams
): Promise<LoggedInUserTypeResult> {
  // Do not trim: web login passed the raw email to getUserByEmail /
  // getExhibitorByEmail / getSponsorByEmail.
  const email = params.email ?? "";
  const normalizedEmail = email.toLowerCase();

  try {
    let resolvedEventId = params.eventId;
    if (!resolvedEventId && params.eventSlug) {
      const event = await prisma.event.findUnique({
        where: { slug: params.eventSlug },
        select: { id: true },
      });
      resolvedEventId = event?.id;
    }

    let userId = params.userId;
    if (!userId && normalizedEmail) {
      const user = await prisma.user.findFirst({
        where: { email: normalizedEmail },
        select: { id: true },
      });
      userId = user?.id;
    }

    const [profile, exhibitorByEmail, sponsorByEmail, teamExhibitor, teamSponsor] =
      await Promise.all([
        userId && resolvedEventId
          ? prisma.profile.findFirst({
              where: { userId, eventId: resolvedEventId },
              select: {
                participantGroups: { select: { type: true } },
              },
            })
          : Promise.resolve(null),
        email && resolvedEventId
          ? prisma.exhibitor.findFirst({
              where: { email, eventId: resolvedEventId },
              select: { id: true },
            })
          : Promise.resolve(null),
        email && resolvedEventId
          ? prisma.sponsor.findFirst({
              where: { email, eventId: resolvedEventId },
              select: { id: true },
            })
          : Promise.resolve(null),
        userId && resolvedEventId
          ? prisma.exhibitor.findFirst({
              where: { eventId: resolvedEventId, teamIds: { has: userId } },
              select: { id: true },
            })
          : Promise.resolve(null),
        userId && resolvedEventId
          ? prisma.sponsor.findFirst({
              where: { eventId: resolvedEventId, teamIds: { has: userId } },
              select: { id: true },
            })
          : Promise.resolve(null),
      ]);

    const groupTypes = profile?.participantGroups.map((group) => group.type) ?? [];

    return resolveLoggedInUserTypeDecision({
      userId,
      groupTypes,
      exhibitorByEmail,
      sponsorByEmail,
      teamExhibitor,
      teamSponsor,
    });
  } catch (error) {
    console.error(error);
    return {
      error: (error as Error).message,
    };
  }
}

/** Web login / reset-password signature. */
export const getLoggedInUserType = async (
  email: string,
  eventId?: string,
  eventSlug?: string
): Promise<LoggedInUserTypeResult> =>
  resolveLoggedInUserType({ email, eventId, eventSlug });
